import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useEffect } from "react";
import useErrorHandler from "src/hooks/utility/useErrorHandler";

/**
 * Standard return type for all data-fetching hooks.
 * Modeled after GraphQL client conventions (Apollo/urql):
 * - `data`: the resolved value, or `undefined` while loading / on error
 * - `loading`: true while the initial fetch is in flight
 * - `error`: the Error object if the query failed, otherwise `null`
 * - `refetch`: manually re-trigger the query
 * - `isFetching`: true during initial load or a background refetch
 */
export type AppQueryResult<T> = {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isFetching?: boolean;
};

/**
 * Thin wrapper around `useQuery` that enforces a consistent return shape
 * and automatically surfaces errors to the user via `useErrorHandler`.
 *
 * Every data hook should use this instead of calling `useQuery` directly.
 */
export default function useAppQuery<T>(
  options: UseQueryOptions<T, Error, T, QueryKey>
): AppQueryResult<T> {
  const errorHandler = useErrorHandler();
  const query = useQuery<T, Error, T, QueryKey>(options);

  useEffect(() => {
    if (query.error) {
      errorHandler(query.error);
    }
  }, [query.error, errorHandler]);

  return {
    data: query.data,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
